/*
  Plays the product gallery's videos as the shopper reaches them.

  This replaces a page-load hack that clicked every deferred-media poster on the
  page, which downloaded every video — including the closed zoom modal's copies —
  before the PDP had finished painting. Nothing here is required for the gallery
  to work: a video keeps its poster until autoplay has actually started, so the
  page fails open to tap-to-play.
*/
(() => {
  const GALLERY_MEDIA = '.product__media-list deferred-media';

  /*
    The poster must outlive loadContent: loading alone hides it (the [loaded]
    rule in component-deferred-media.css), while play() can still be refused
    (Low Power Mode, Data Saver). This class holds the poster on screen until
    playback really starts; on refusal the poster stays as the way in.
  */
  const PENDING_CLASS = 'video-autoplay-pending';

  /*
    No pre-load margin, and a real slice of the slide has to be on screen. A
    200px margin sounds harmless, but on a desktop PDP the gallery is short
    enough that the video slide already falls inside it at page load — so a
    6.4MB mp4 downloaded before the shopper had scrolled at all, which is the
    exact cost this file exists to avoid. A beat of delay on reaching the slide
    is the better trade.
  */
  const APPROACH_MARGIN = '0px';
  const VISIBLE_RATIO = 0.2;

  customElements.whenDefined('deferred-media').then(() => {
    // A shopper who has asked for less motion gets the poster and taps to play.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /*
      Only slides whose template holds a native <video>. An external (YouTube/
      Vimeo) slide is an iframe: play() and pause() have nothing to reach, its
      URL carries autoplay for the poster tap, and arming it would only hide
      the play affordance on a slide this file can never drive.
    */
    const holdsNativeVideo = (media) => {
      if (media.querySelector('video')) return true;
      const template = media.querySelector('template');
      return Boolean(template && template.content && template.content.querySelector('video'));
    };

    const targets = Array.from(document.querySelectorAll(GALLERY_MEDIA)).filter(
      (media) => !media.closest('.product-media-modal') && holdsNativeVideo(media)
    );
    if (targets.length === 0) return;

    const visible = new Set();

    const play = (media) => {
      const video = media.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.playsInline = true;
      video
        .play()
        .then(() => media.classList.remove(PENDING_CLASS))
        .catch(() => {
          // Autoplay refused (low power mode, a stricter policy): the pending
          // class keeps the poster visible, and the tap below is the way in.
        });
    };

    /*
      DeferredMedia.loadContent and ModalDialog both call pauseAllMedia, so any
      one load or modal open stops every other gallery video. Re-driving the
      whole set from what is on screen is what keeps those side effects from
      leaving a video paused mid-viewport — and every load must land before the
      first play(), or a later slide's loadContent pauses the video an earlier
      iteration just started.
    */
    const sync = () => {
      targets.forEach((media) => {
        if (visible.has(media)) {
          if (!media.getAttribute('loaded')) {
            media.classList.add(PENDING_CLASS);
            // `false` keeps the load from stealing focus and scrolling the page.
            media.loadContent(false);
          }
        } else {
          const video = media.querySelector('video');
          if (video) video.pause();
        }
      });

      targets.forEach((media) => {
        if (visible.has(media)) play(media);
      });
    };

    // The observer's rule, measured now for the hand-off below: clipped by the
    // viewport and by every scrolling ancestor (the mobile slider), as the
    // observer clips by its root.
    const onScreen = (media) => {
      const rect = media.getBoundingClientRect();
      const area = rect.width * rect.height;
      if (!area) return false;
      const clip = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
      for (let node = media.parentElement; node && node !== document.body; node = node.parentElement) {
        const { overflowX, overflowY } = getComputedStyle(node);
        if (overflowX === 'visible' && overflowY === 'visible') continue;
        const box = node.getBoundingClientRect();
        clip.left = Math.max(clip.left, box.left);
        clip.top = Math.max(clip.top, box.top);
        clip.right = Math.min(clip.right, box.right);
        clip.bottom = Math.min(clip.bottom, box.bottom);
      }
      const width = Math.min(rect.right, clip.right) - Math.max(rect.left, clip.left);
      const height = Math.min(rect.bottom, clip.bottom) - Math.max(rect.top, clip.top);
      return width > 0 && height > 0 && (width * height) / area >= VISIBLE_RATIO;
    };

    targets.forEach((media) => {
      // If autoplay was refused the poster stayed; a tap on it is the user
      // gesture play() needs to be allowed.
      media.addEventListener('click', (event) => {
        if (!media.classList.contains(PENDING_CLASS)) return;
        if (!event.target.closest('.deferred-media__poster')) return;
        // Play NOW, inside the gesture — WebKit scopes its activation token to
        // the handler's synchronous run, and Low Power Mode (the case this
        // poster exists for) requires it. DeferredMedia's own poster handler
        // also fires on this click and calls pauseAllMedia, so the deferred
        // sync then repairs whatever it stopped; its re-play of this slide is
        // a no-op.
        play(media);
        requestAnimationFrame(sync);
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // The ratio, not isIntersecting: a slide one pixel into view is
          // intersecting, and that is what used to start the download early.
          if (entry.intersectionRatio >= VISIBLE_RATIO) {
            visible.add(entry.target);
          } else {
            visible.delete(entry.target);
          }
        });
        sync();
      },
      { rootMargin: APPROACH_MARGIN, threshold: [0, VISIBLE_RATIO] }
    );

    targets.forEach((media) => observer.observe(media));

    // The modal pauses the gallery behind it on open; closing has to hand
    // playback back to whatever is on screen. ModalDialog.hide() dispatches
    // modalClosed and THEN calls pauseAllMedia, so a synchronous sync here
    // would be re-paused within the same task — hence the animation frame.
    document.body.addEventListener('modalClosed', () => requestAnimationFrame(sync));

    // MediaGallery.playActiveMedia paused everything and played its own slide:
    // restart the other loaded videos on screen, measured (`visible` is stale
    // after a reorder). Not for the quick-add modal's gallery: the page stays
    // paused behind an open modal.
    document.body.addEventListener('activeMediaChanged', (event) => {
      if (!targets.some((media) => event.target.contains(media))) return;
      targets.forEach((media) => {
        if (media.querySelector('video') && onScreen(media)) play(media);
      });
    });
  });
})();
